import { useEffect, useRef, useState } from "react";
import { useSettings } from "../settings/context";
import { useTheme } from "../theme/useTheme";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";
import { dataUrlToFile, uploadEditorImage } from "../api/upload";
import {
  adoptMenus,
  CANVAS_HEIGHT,
  hasInlineImages,
  interceptImageUpload,
  replaceInlineImages,
  TOOLS,
} from "./htmlEditor";

/**
 * A BLOCK OF THE ORGANISER'S OWN HTML — laid out visually, or written by hand.
 *
 * Both, and that is the requirement rather than a convenience: clients want to lay
 * their message out themselves, and some of them want the markup. So the field has
 * two modes, and the one they leave it in is remembered — the same arrangement the
 * back office already offers on these very settings, and the same editor (SunEditor),
 * so what they type here behaves like what they type there. ⚠️ NOT the same VERSION:
 * the back office is on 2.47 through the React wrapper, this is 3.x on the core, and
 * the two differ on toolbar names and on how handlers are registered — see below.
 *
 * ⚠️ THE HTML MODE ALWAYS SHOWS WHAT IS STORED. A visual editor NORMALISES markup: a
 * hand-written `<div class="…">` can come back rewritten. So the raw mode is not a
 * fallback, it is the source of truth — whatever the editor did, they can see it and
 * put it back.
 *
 * ⚠️ MERELY LOOKING AT A BLOCK IN VISUAL MODE MUST NOT REWRITE IT. The editor is given
 * the stored HTML at mount, and the first change it reports is ignored: without that,
 * opening a hand-written block would silently replace it with the editor's own idea
 * of the same thing, and nothing would have been edited.
 *
 * ⚠️ AND IT IS LOADED ON DEMAND. The editor is 1.08 MB across four chunks — as much as
 * the whole application again (measured on this build: 468 kB core, 485 kB plugins,
 * 118 kB stylesheet, 6 kB French labels, against 1038 kB for the app). A participant
 * filling in a form on their phone must never download an editor they cannot open, so
 * it arrives with the dialog, not with the page — verified on the built site: a
 * visitor requests none of them, an organiser requests them when the dialog opens and
 * not before.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

const MODE_KEY = "gk-fo-v2-html-mode";

type Mode = "layout" | "html";

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "html" ? "html" : "layout";
  } catch {
    return "layout";
  }
}

/** The visual editor, mounted on a plain textarea SunEditor takes over. */
function Layout({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const host = useRef<HTMLTextAreaElement>(null);
  const [failed, setFailed] = useState(false);

  /**
   * THE CANVAS CARRIES THE EVENT'S PALETTE — and only the canvas.
   *
   * The dialog is part of the TOOL and keeps the tool's colours; `useTheme`'s own
   * reasoning says why (an organiser configuring a navy event must not end up with a
   * navy editor on a navy site). But what is inside the frame is a PREVIEW OF THE
   * PAGE, and the page's ink is the organiser's, not the stylesheet's default. The
   * dialog lives outside `.site`, so it inherits none of it: measured, the canvas
   * came out dark brown where the page renders mid-brown.
   */
  const [canvas, setCanvas] = useState<HTMLDivElement | null>(null);
  useTheme(canvas);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let editor: { destroy: () => void } | null = null;
    let cancelled = false;
    // The first change is the editor loading what it was given, not somebody typing.
    let loaded = false;

    (async () => {
      try {
        /**
         * ⚠️ `suneditor/css/editor`, and not a path under `dist`. The package's
         * `exports` map publishes its stylesheet under that name ALONE, so a deep
         * path fails the build outright — and the file that does exist on disk,
         * `dist/suneditor.min.css`, is not reachable through it.
         */
        const [{ default: create }, { default: plugins }, { default: lang }] =
          await Promise.all([
            import("suneditor"),
            /**
             * ⚠️ THE PLUGINS ARE NOT OPTIONAL. Half of any real toolbar is plugins —
             * `align`, `list`, `fontColor`, `backgroundColor`, `link`, `image` — and a
             * button whose plugin is missing does not degrade, `create` THROWS: "The
             * button name of a plugin that does not exist. [align]", and no editor
             * appears at all. The React wrapper the back office uses imports them for
             * you, which is why a toolbar copied from there fails here.
             */
            import("suneditor/plugins"),
            /**
             * ⚠️ AND THE TOOLBAR NOW SPEAKS. As long as it was icons alone, the
             * editor's own English never showed; « Formats », « Police » and « Taille »
             * are WORDS, and three English labels in the middle of a French panel are
             * the only ones on the screen. One language file, 8 kB, loaded with the
             * rest. `suneditor/langs/*` is a published subpath and the core itself
             * imports `langs/en` exactly this way, so the interop is the library's own.
             */
            import("suneditor/langs/fr"),
            import("suneditor/css/editor"),
          ]);
        if (cancelled) return;
        const instance = create.create(el, {
          plugins,
          lang,
          /**
           * The toolbar and the lists its dropdowns read — `htmlEditor.ts`, with the
           * reasoning and a test that confronts every name with the installed package.
           */
          ...TOOLS,
          height: CANVAS_HEIGHT,
          value,
          /**
           * ⚠️ THE HANDLERS ARE AN OPTION, not something you hang on the instance
           * afterwards — `instance.events` is undefined at that point and assigning to
           * it throws. And `onChange` is handed an OBJECT: `options.d.ts` documents
           * `events`, `events.d.ts` declares `onChange({ $, frameContext, data })`.
           */
          events: {
            onChange: ({ data }: { data: string }) => {
              if (!loaded) {
                loaded = true;
                return;
              }
              onChange(data);
            },
            /**
             * ⚠️ AUCUNE IMAGE EN BASE64 DANS LES RÉGLAGES : chacune part dans le seau public,
             * recompressée, et le HTML n'en garde que l'adresse. Voir `htmlEditor.ts`.
             */
            onImageUploadBefore: interceptImageUpload(uploadEditorImage),
            onPaste: async ({
              $,
              data,
            }: {
              $: { ui?: { alertOpen?: (message: string, kind: "" | "error" | "success") => void } };
              data: string;
            }) => {
              if (!hasInlineImages(data)) return undefined;
              const { html, failed } = await replaceInlineImages(
                data,
                uploadEditorImage,
                dataUrlToFile,
              );
              if (failed) {
                $.ui?.alertOpen?.(
                  failed === 1
                    ? "Une image collée n'a pas pu être envoyée : elle a été retirée."
                    : `${failed} images collées n'ont pas pu être envoyées : elles ont été retirées.`,
                  "error",
                );
              }
              return html || false;
            },
          },
        });
        /**
         * ⚠️ AVANT TOUT LE RESTE : LES MENUS SONT AILLEURS. SunEditor pend ses
         * calques — les listes de « Police », « Taille », les couleurs, ses modales
         * de lien et d'image — à `document.body`, donc HORS de cette modale, et une
         * modale native rend inerte tout ce qui n'est pas dedans. Ils s'ouvraient et
         * le clic les traversait. `adoptMenus` explique le pourquoi en entier.
         */
        adoptMenus(instance, el);
        // `onChange` does not fire for the initial contents in every version; mark it
        // loaded on the next tick so a real keystroke is never swallowed.
        setTimeout(() => {
          loaded = true;
        }, 0);
        editor = instance as unknown as { destroy: () => void };
      } catch (cause) {
        // A chunk that fails to load must not leave an empty box: the raw mode is
        // right there and it can do everything.
        console.error("[éditeur]", cause);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      try {
        editor?.destroy();
      } catch {
        /* already gone with the dialog */
      }
    };
    // Mounted once per opening: re-running it would rebuild the editor under the
    // organiser's cursor on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <p className="row__h">
        L'éditeur n'a pas pu être chargé. Passez en HTML — il permet tout ce que
        l'éditeur permet.
      </p>
    );
  }
  return (
    <div className="edit__canvas" ref={setCanvas}>
      <textarea ref={host} defaultValue={value} />
    </div>
  );
}

/**
 * `title` : le nom de la ligne qui ouvre la fenêtre. Écrit en dur, l'en-tête disait
 * « Message d'introduction » sur CHAQUE champ HTML, le pied de page compris, et même
 * sur celui de l'écran de connexion une fois renommé « Bloc d'introduction » (tâche 286).
 */
export function HtmlField({
  settingKey,
  title,
}: {
  settingKey: string;
  title: string;
}) {
  const { value, write } = useSettings();
  const current = value(settingKey);
  const html = typeof current === "string" ? current : "";

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(readMode);
  const dialog = useRef<HTMLDialogElement>(null);

  // The row's preview is a piece of the PAGE, so it wears the page's palette — see
  // `.html__prev`, and the same reasoning as the canvas below.
  const [preview, setPreview] = useState<HTMLDivElement | null>(null);
  useTheme(preview);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const choose = (next: Mode) => {
    setMode(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      /* it will not survive a reload; the field stays correct */
    }
  };

  return (
    <div className="html">
      {/* What is stored, rendered — an organiser must be able to see their block
          without opening anything. Empty says so rather than showing a void. */}
      {html.trim() ? (
        <Prose
          html={html}
          className="html__prev"
          ref={setPreview}
          />
      ) : (
        <p className="row__h">Rien n'est affiché pour l'instant.</p>
      )}

      <div className="html__acts">
        <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
          {html.trim() ? "Modifier" : "Rédiger"}
        </Button>
        {html.trim() && (
          <Button
            type="button"
            variant="ghost"
            // Discret : c'est le geste qui détruit, pas celui qu'on cherche.
            className="btn--off"
            onClick={() => write(settingKey, "")}
          >
            Effacer
          </Button>
        )}
      </div>

      <dialog
        ref={dialog}
        className="ask edit"
        onCancel={(e) => {
          e.preventDefault();
          setOpen(false);
        }}
      >
        <div className="ask__b">
          <div className="edit__h">
            <b>{title}</b>
            <div className="oseg">
              <button
                type="button"
                aria-pressed={mode === "layout"}
                onClick={() => choose("layout")}
              >
                Mise en page
              </button>
              <button
                type="button"
                aria-pressed={mode === "html"}
                onClick={() => choose("html")}
              >
                HTML
              </button>
            </div>
          </div>

          {/* Keyed on the mode: switching REBUILDS the editor on what is stored now,
              instead of leaving it showing what it held before the other mode wrote. */}
          {open && mode === "layout" && (
            <Layout
              key={`layout-${open}`}
              value={html}
              onChange={(next) => write(settingKey, next)}
            />
          )}

          {mode === "html" && (
            <textarea
              className="edit__raw"
              // ⚠️ LA MÊME BOÎTE QUE LE CANEVAS, et donc la même valeur — les deux
              // modes sont un seul champ, basculer ne doit pas faire sauter la
              // modale. La seule dimension du panneau qui ne soit pas dans la
              // feuille : elle est lue aussi par l'éditeur, qui ne lit pas de CSS.
              style={{ minHeight: CANVAS_HEIGHT }}
              spellCheck={false}
              value={html}
              aria-label="HTML du message"
              onChange={(e) => write(settingKey, e.target.value)}
            />
          )}

          <div className="ask__a">
            {/* Le bouton du PANNEAU, pas celui du site : `variant="ghost"` le fait
                tomber dans `.panel .btn--ghost`, donc exactement la pastille de
                « Modifier ». En principal il faisait 93×44 px en bleu de marque —
                l'action la plus voyante de l'écran pour un simple congé, alors que
                rien n'est en attente : chaque frappe est déjà dans le réglage. */}
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Fermer
            </Button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
